import { useCallback, useEffect, useLayoutEffect, useState } from "react";

// Between the button and its popover
const ANCHOR_GAP_PX = 6;
// Mirrors $chart-viewport-gutter in _variables.sass: a popover keeps this far from the screen edges
const VIEWPORT_GUTTER_PX = 16;

const HIDDEN_STYLE = { visibility: "hidden" };

/**
 * Place of a popover with `position: fixed` next to its button: under it, or
 * over it when there is no room below, inside the screen. Fixed positioning
 * lets it stick out of a modal that scrolls its content. It follows a
 * resize and any scroll, and closes on a press outside the button and itself.
 *
 * @param {object} params
 * @param {React.RefObject<HTMLElement>} params.anchorRef - the button
 * @param {React.RefObject<HTMLElement>} params.popoverRef
 * @param {boolean} params.isOpen
 * @param {() => void} params.onClose
 * @returns {object} inline style of the popover: hidden until it is measured
 */
const useAnchoredPopover = ({ anchorRef, popoverRef, isOpen, onClose }) => {
  const [position, setPosition] = useState(null);

  const measure = useCallback(() => {
    const anchor = anchorRef.current;
    const popover = popoverRef.current;

    if (!anchor || !popover) return;

    const anchorRect = anchor.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = popover;
    const below = anchorRect.bottom + ANCHOR_GAP_PX;
    const above = anchorRect.top - ANCHOR_GAP_PX - height;
    const fitsBelow = below + height <= window.innerHeight - VIEWPORT_GUTTER_PX;
    const maxLeft = window.innerWidth - VIEWPORT_GUTTER_PX - width;

    setPosition({
      top: fitsBelow || above < VIEWPORT_GUTTER_PX ? below : above,
      left: Math.max(VIEWPORT_GUTTER_PX, Math.min(anchorRect.left, maxLeft)),
    });
  }, [anchorRef, popoverRef]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);

      return undefined;
    }

    measure();
    window.addEventListener("resize", measure);
    // Capture: the modal content scrolls, not the window
    window.addEventListener("scroll", measure, true);

    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [isOpen, measure]);

  useEffect(() => {
    if (!isOpen) return undefined;

    // A pointer press, not mousedown: a finger that drags outside sends no mouse events
    const handlePointerDown = (event) => {
      const isInside = [anchorRef.current, popoverRef.current].some((element) =>
        element?.contains(event.target),
      );

      if (!isInside) onClose();
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, onClose, anchorRef, popoverRef]);

  return position || HIDDEN_STYLE;
};

export default useAnchoredPopover;
